/* Theme Provider for styled-components.
  the default export 'theme' is considered legacy but is here for backwards
  compatibility reasons. This is accessed in all packages using context.

*/
import type { DefaultTheme } from "styled-components";
import type {} from "styled-components/cssprop";
import {
  allTokens,
  getTokenValue,
  fontFamilyTokens,
  fontWeightTokens,
  fontSizeTokens,
  lineHeightTokens,
  radiusTokens,
  spacingTokens,
  borderPrimitiveTokens,
  buttonSemanticTokens,
  buttonIconSemanticTokens,
  colorTextSemanticTokens,
  colorBgUiSemanticTokens,
  colorBorderSemanticTokens,
  colorIconSemanticTokens,
  colorPrimitiveTokens,
  widthTokens,
  AllTokenKeys,
} from "./designTokens"; // Adjust path as needed

// --- Token System Interfaces ---
interface TokenButtonStateProperties {
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  iconColor?: string;
}

interface TokenButtonThemeProperties {
  default: TokenButtonStateProperties;
  hover: TokenButtonStateProperties;
  active: TokenButtonStateProperties;
  focus?: TokenButtonStateProperties;
  disabled: Partial<TokenButtonStateProperties>;
  selected?: Partial<TokenButtonStateProperties>;
  padding: string;
  gap: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
  borderRadius: string;
  borderWidth: string;
  loadingIconSize: string;
}

interface TokenButtonIconThemeProperties {
  default: Pick<
    TokenButtonStateProperties,
    "backgroundColor" | "borderColor" | "iconColor"
  >;
  hover: Pick<
    TokenButtonStateProperties,
    "backgroundColor" | "borderColor" | "iconColor"
  >;
  disabled: Partial<
    Pick<
      TokenButtonStateProperties,
      "backgroundColor" | "borderColor" | "iconColor"
    >
  >;
  destructive: {
    default: Pick<
      TokenButtonStateProperties,
      "backgroundColor" | "borderColor" | "iconColor"
    >;
    hover: Pick<
      TokenButtonStateProperties,
      "backgroundColor" | "borderColor" | "iconColor"
    >;
    disabled: Partial<
      Pick<
        TokenButtonStateProperties,
        "backgroundColor" | "borderColor" | "iconColor"
      >
    >;
  };
  borderRadius: string;
}

interface TokenGlobalDisabledState {
  backgroundColor: string;
  textColor: string;
  borderColor?: string;
  opacity: string;
}

// Defines the shape of our token categories
interface TokenButtonSizeProperties {
  fontSize: string;
  lineHeight: string;
  padding: string;
  loadingIconSize: string;
}

interface TokenButtonSizeWithGapProperties extends TokenButtonSizeProperties {
  gap: string;
}

interface ThemeTokenCategories {
  buttons: {
    primary: TokenButtonThemeProperties;
    secondary: TokenButtonThemeProperties;
    tertiary: TokenButtonThemeProperties;
    destructive: TokenButtonThemeProperties;
    ghost: TokenButtonThemeProperties;
    primaryLarge: TokenButtonSizeProperties;
    primaryMedium: TokenButtonSizeProperties;
    primarySmall: TokenButtonSizeProperties;
    primaryXSmall: TokenButtonSizeProperties;
    secondaryLarge: TokenButtonSizeProperties;
    secondaryMedium: TokenButtonSizeProperties;
    secondarySmall: TokenButtonSizeProperties;
    secondaryXSmall: TokenButtonSizeProperties;
    tertiaryLarge: TokenButtonSizeProperties;
    tertiaryMedium: TokenButtonSizeWithGapProperties;
    tertiarySmall: TokenButtonSizeProperties;
    destructiveLarge: TokenButtonSizeProperties;
    destructiveMedium: TokenButtonSizeProperties;
    destructiveSmall: TokenButtonSizeProperties;
    destructiveXSmall: TokenButtonSizeProperties;
    buttonIcon: TokenButtonIconThemeProperties;
  };
  semanticColors: {
    textPrimary: string;
    textSecondary: string;
    textTertiary: string;
    textPlaceholder: string;
    textError: string;
    textWarning: string;
    textSuccess: string;
    textInfo: string;
    textInverse: string;
    textBrand: string;
    bgSurfaceDefault: string;
    bgSurfaceSubtle: string;
    bgSurfaceDark: string;
    bgSurfaceDisabled: string;
    bgSurfaceError: string;
    bgSurfaceWarning: string;
    bgSurfaceSuccess: string;
    bgSurfaceInfo: string;
    bgSurfaceBrand: string;
    bgOverlay: string;
    borderLightest: string;
    borderLighter: string;
    borderLight: string;
    borderDark: string;
    borderInputDefault: string;
    borderInputActive: string;
    borderError: string;
    iconPrimary: string;
    iconSecondary: string;
    iconDisabled: string;
    iconWhite: string;
    iconSuccess: string;
    iconError: string;
    iconBrand: string;
  };
  primitiveTokens: {
    fontFamilies: Record<keyof typeof fontFamilyTokens, string>;
    fontWeights: Record<keyof typeof fontWeightTokens, string>;
    fontSizes: Record<keyof typeof fontSizeTokens, string>;
    lineHeights: Record<keyof typeof lineHeightTokens, string>;
    colors: Record<keyof typeof colorPrimitiveTokens, string>;
    spacing: Record<keyof typeof spacingTokens, string>;
    radii: Record<keyof typeof radiusTokens, string>;
    borders: Record<keyof typeof borderPrimitiveTokens, string>;
    widths: Record<keyof typeof widthTokens, string>;
  };
  disabledGlobal: TokenGlobalDisabledState;
}

declare module "styled-components" {
  // Merge ThemeTokenCategories with legacy props and new top-level resolvers for DefaultTheme
  export interface DefaultTheme extends ThemeTokenCategories {
    // Legacy properties
    /**
     * @deprecated Use primitiveTokens.colors instead for new code. This is here for backwards compatibility reasons.
     */
    colors: { [key: string]: string };
    fontFamily: string;
    /**
     * @deprecated Use primitiveTokens.fontSizes instead for new code. This is here for backwards compatibility reasons.
     */
    fontSizes: { [key: string]: string };
    /**
     * @deprecated Use primitiveTokens.fontWeights instead for new code. This is here for backwards compatibility reasons.
     */
    fontWeights: { [key: string]: string };
    brandColor: string;
    primaryBG: string;
    secondaryBG: string;
    tertiaryBG: string;
    primaryButtonBG: string;
    primaryButtonHover: string;
    primaryButtonActive: string;
    secondaryButtonBG: string;
    secondaryButtonHover: string;
    secondaryButtonActive: string;
    tertiaryButtonBG: string;
    tertiaryButtonSelected: string;
    destructiveButtonBG: string;
    destructiveButtonHover: string;
    destructiveButtonActive: string;
    disabledButtonBG: string;
    disabledInput: string;
    toggleBG: string;
    activeToggleBG: string;
    badgeColor: string;
    successIconColor: string;
    errorIconColor: string;
    badgeTextColor: string;
    footerBg: string;
    secondaryInputBG: string;
    tooltipBG: string;
    primaryTextColor: string;
    secondaryTextColor: string;
    tertiaryTextColor: string;
    invertTextColor: string;
    warningTextColor: string;
    destructiveTextColor: string;
    navLinkColor: string;
    navIconActive: string;
    navLinkActiveColor: string;
    primaryLinkColor: string;
    primaryLinkHover: string;
    secondaryLinkColor: string;
    secondaryLinkHover: string;
    tertiaryLinkColor: string;
    tertiaryLinkHover: string;
    disabledLinkColor: string;
    primaryButtonTextColor: string;
    secondaryButtonTextColor: string;
    tertiaryButtonTextColor: string;
    destructiveButtonTextColor: string;
    disabledButtonTextColor: string;
    primaryIconColor: string;
    secondaryIconColor: string;
    tertiaryIconColor: string;
    primaryIconBackground: string;
    primaryBorder: string;
    secondaryBorder: string;
    tertiaryBorder: string;
    primaryButtonBorder: string;
    secondaryButtonBorder: string;
    secondaryButtonHoverBorder: string;
    selectedBorder: string;
    errorColor: string;
    shadowColor: string;
    overlayColor: string;
    publicNavBorderActive: string;
    publicNavBorder: string;
    publicLogoBGColor: string;
    publicLogoUrl: string;
    portalLogoUrl: string;
    theme_name: string;

    // Top-level resolvers and token map
    resolve: <T extends keyof typeof import("./designTokens").allTokens>(
      tokenNameOrValue: T
    ) => string;
    resolveSizeToNumber: <
      T extends keyof typeof import("./designTokens").allTokens
    >(
      tokenNameOrValue: T
    ) => number;
    allTokensForTheme: Record<AllTokenKeys, string>;

    // Token categories for structured access
    tokens: ThemeTokenCategories;
  }
}

//MediaQuery breakpoints.
export const screenSize = {
  xl: `(min-width: 1025px)`,
  large: `(max-width: 1024px)`, //For iPad Pro...
  mLarge: `(max-width: 991px)`, //For iPad air...
  medium: `(max-width: 768px)`, //For regular iPad, Tablets...
  small: `(max-width: 450px)`, //For Mobile
};

const themeDefaults = {
  fontFamily: "Inter",
  /**
   * @deprecated
   */
  fontWeights: {
    large: "600",
    medium: "500",
    regular: "400",
    small: "300",
  },
  /**
   * @deprecated
   */
  fontSizes: {
    // Unless a font-size has a suffix of -medium or -small it is shown all screens.
    "xl-4": "60px",
    "xl-3": "48px",
    "xl-2": "36px",
    xl: "28px",
    "xl-small": "23px",
    large: "19px",
    medium: "15px",
    regular: "14px",
    small: "13px",
    xs: "11px",
  },
  /**
   * Do not use this colors object for new code.
   * @deprecated
   */
  colors: {
    white: "#fff",
    black: "#000",
    primaryBlue: "#0090e3", // The Primary blue color in the app.
    secondaryBlue: "#ecf8ff", // Secondary Blue
    darkBlue: "#0f1d2b",
    secondaryLightGray: "#cfd1d4",
    errorRedBorder: "#cb2020", // Used for border color on form fields in error state
    activeGreen: "#1fb45a", // used on toggle switches in the on position
    borderBlue: "#99d2f3",
    grayText: "#6f777f",
    darkText: "#0f1d2b",
    darkBorder: "#0f1d2b",
    mediumGreen: "#1ba150",
    statusTextGreen: "#00a545",
    burntOrange: "#d44800",
    moreBurntOrange: "#c45113",
    productStatusOrange: "#DB5B16",
    productStatusYellow: "#F6B200",
    "orange-3": "#83360d",
    "orange-4": "#f6b200",
    "red-1": "#c70009",
    "red-2": "#b61c1c",
    "red-3": "#791313",
    "gray-1": "#9fa4aa",
    "gray-2": "#6d7780",
    "gray-3": "#f5f7f8",
    "gray-4": "#cfd1d4",
    "green-3": "#126c36",
    "blue-3": "#0073b5",
    "blue-4": "#005688",
    "blue-5": "#d9effc",
    "blue-6": "#c8e8fa",
  },
};
export const AG3Branding = {
  brandColor: "#0090e3",
  portalLogoUrl: "",
  publicLogoUrl: "",
  favIconUrl: "",
  customFontFamily: "",
};

// Define the token categories separately to avoid repetition and ensure type correctness
const themeTokenCategories: ThemeTokenCategories = {
  buttons: {
    primary: {
      default: {
        backgroundColor: buttonSemanticTokens.color_button_bg_primary_enabled,
        textColor: buttonSemanticTokens.color_button_text_primary_enabled,
        borderColor: buttonSemanticTokens.color_button_border_primary_enabled,
        iconColor: buttonSemanticTokens.color_button_icon_primary_enabled,
      },
      hover: {
        backgroundColor: buttonSemanticTokens.color_button_bg_primary_hover,
        textColor: buttonSemanticTokens.color_button_text_primary_hover,
        borderColor: buttonSemanticTokens.color_button_border_primary_hover,
        iconColor: buttonSemanticTokens.color_button_icon_primary_enabled,
      },
      active: {
        backgroundColor: buttonSemanticTokens.color_button_bg_primary_active,
        textColor: buttonSemanticTokens.color_button_text_primary_active,
        borderColor: buttonSemanticTokens.color_button_border_primary_active,
        iconColor: buttonSemanticTokens.color_button_icon_primary_enabled,
      },
      disabled: {
        backgroundColor: buttonSemanticTokens.color_button_bg_primary_disabled,
        textColor: buttonSemanticTokens.color_button_text_primary_disabled,
        borderColor: buttonSemanticTokens.color_button_border_primary_disabled,
      },
      padding: buttonSemanticTokens.button_padding_base,
      gap: buttonSemanticTokens.button_gap,
      fontFamily: buttonSemanticTokens.button_font_family,
      fontSize: buttonSemanticTokens.button_font_size_base,
      fontWeight: buttonSemanticTokens.button_font_weight_medium,
      lineHeight: buttonSemanticTokens.button_line_height_base,
      borderRadius: buttonSemanticTokens.radius_button_standard,
      borderWidth: buttonSemanticTokens.button_border_width,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    secondary: {
      default: {
        backgroundColor: buttonSemanticTokens.color_button_bg_secondary_enabled,
        textColor: buttonSemanticTokens.color_button_text_secondary_enabled,
        borderColor: buttonSemanticTokens.color_button_border_secondary_enabled,
        iconColor: buttonSemanticTokens.color_button_icon_secondary_enabled,
      },
      hover: {
        backgroundColor: buttonSemanticTokens.color_button_bg_secondary_hover,
        textColor: buttonSemanticTokens.color_button_text_secondary_hover,
        borderColor: buttonSemanticTokens.color_button_border_secondary_hover,
        iconColor: buttonSemanticTokens.color_button_icon_secondary_enabled,
      },
      active: {
        backgroundColor: buttonSemanticTokens.color_button_bg_secondary_active,
        textColor: buttonSemanticTokens.color_button_text_secondary_active,
        borderColor: buttonSemanticTokens.color_button_border_secondary_active,
        iconColor: buttonSemanticTokens.color_button_icon_secondary_enabled,
      },
      disabled: {
        backgroundColor:
          buttonSemanticTokens.color_button_bg_secondary_disabled,
        textColor: buttonSemanticTokens.color_button_text_secondary_disabled,
        borderColor:
          buttonSemanticTokens.color_button_border_secondary_disabled,
      },
      padding: buttonSemanticTokens.button_padding_base,
      gap: buttonSemanticTokens.button_gap,
      fontFamily: buttonSemanticTokens.button_font_family,
      fontSize: buttonSemanticTokens.button_font_size_base,
      fontWeight: buttonSemanticTokens.button_font_weight_medium,
      lineHeight: buttonSemanticTokens.button_line_height_base,
      borderRadius: buttonSemanticTokens.radius_button_standard,
      borderWidth: buttonSemanticTokens.button_border_width,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    tertiary: {
      default: {
        backgroundColor: buttonSemanticTokens.color_button_bg_tertiary_enabled,
        textColor: buttonSemanticTokens.color_button_text_tertiary_enabled,
        borderColor: buttonSemanticTokens.color_button_border_tertiary_enabled,
        iconColor: buttonSemanticTokens.color_button_icon_tertiary_enabled,
      },
      hover: {
        backgroundColor: buttonSemanticTokens.color_button_bg_tertiary_hover,
        textColor: buttonSemanticTokens.color_button_text_tertiary_hover,
        borderColor: buttonSemanticTokens.color_button_border_tertiary_hover,
        iconColor: buttonSemanticTokens.color_button_icon_tertiary_enabled,
      },
      active: {
        backgroundColor: buttonSemanticTokens.color_button_bg_tertiary_active,
        textColor: buttonSemanticTokens.color_button_text_tertiary_active,
        borderColor: buttonSemanticTokens.color_button_border_tertiary_active,
        iconColor: buttonSemanticTokens.color_button_icon_tertiary_enabled,
      },
      disabled: {
        backgroundColor: buttonSemanticTokens.color_button_bg_tertiary_disabled,
        textColor: buttonSemanticTokens.color_button_text_tertiary_disabled,
        borderColor: buttonSemanticTokens.color_button_border_tertiary_disabled,
      },
      padding: buttonSemanticTokens.button_padding_base,
      gap: buttonSemanticTokens.button_gap,
      fontFamily: buttonSemanticTokens.button_font_family,
      fontSize: buttonSemanticTokens.button_font_size_base,
      fontWeight: buttonSemanticTokens.button_font_weight_medium,
      lineHeight: buttonSemanticTokens.button_line_height_base,
      borderRadius: buttonSemanticTokens.radius_button_standard,
      borderWidth: buttonSemanticTokens.button_border_width,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    destructive: {
      default: {
        backgroundColor:
          buttonSemanticTokens.color_button_bg_destructive_enabled,
        textColor: buttonSemanticTokens.color_button_text_destructive_enabled,
        borderColor:
          buttonSemanticTokens.color_button_border_destructive_enabled,
        iconColor: buttonSemanticTokens.color_button_icon_destructive_enabled,
      },
      hover: {
        backgroundColor: buttonSemanticTokens.color_button_bg_destructive_hover,
        textColor: buttonSemanticTokens.color_button_text_destructive_hover,
        borderColor: buttonSemanticTokens.color_button_border_destructive_hover,
        iconColor: buttonSemanticTokens.color_button_icon_destructive_hover,
      },
      active: {
        backgroundColor:
          buttonSemanticTokens.color_button_bg_destructive_active,
        textColor: buttonSemanticTokens.color_button_text_destructive_active,
        borderColor:
          buttonSemanticTokens.color_button_border_destructive_active,
        iconColor: buttonSemanticTokens.color_button_icon_destructive_hover,
      },
      disabled: {
        backgroundColor:
          buttonSemanticTokens.color_button_bg_destructive_disabled,
        textColor: buttonSemanticTokens.color_button_text_destructive_disabled,
        borderColor:
          buttonSemanticTokens.color_button_border_destructive_disabled,
      },
      padding: buttonSemanticTokens.button_padding_base,
      gap: buttonSemanticTokens.button_gap,
      fontFamily: buttonSemanticTokens.button_font_family,
      fontSize: buttonSemanticTokens.button_font_size_base,
      fontWeight: buttonSemanticTokens.button_font_weight_medium,
      lineHeight: buttonSemanticTokens.button_line_height_base,
      borderRadius: buttonSemanticTokens.radius_button_standard,
      borderWidth: buttonSemanticTokens.button_border_width,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    ghost: {
      default: {
        backgroundColor: buttonSemanticTokens.color_button_bg_ghost_enabled,
        textColor: buttonSemanticTokens.color_button_text_ghost_enabled,
        borderColor: buttonSemanticTokens.color_button_border_ghost_enabled,
        iconColor: buttonSemanticTokens.color_button_icon_ghost_enabled,
      },
      hover: {
        backgroundColor: buttonSemanticTokens.color_button_bg_ghost_hover,
        textColor: buttonSemanticTokens.color_button_text_ghost_hover,
        borderColor: buttonSemanticTokens.color_button_border_ghost_hover,
        iconColor: buttonSemanticTokens.color_button_icon_ghost_enabled,
      },
      active: {
        backgroundColor: buttonSemanticTokens.color_button_bg_ghost_active,
        textColor: buttonSemanticTokens.color_button_text_ghost_active,
        borderColor: buttonSemanticTokens.color_button_border_ghost_active,
        iconColor: buttonSemanticTokens.color_button_icon_ghost_enabled,
      },
      disabled: {
        backgroundColor: buttonSemanticTokens.color_button_bg_ghost_disabled,
        textColor: buttonSemanticTokens.color_button_text_ghost_disabled,
        borderColor: buttonSemanticTokens.color_button_border_ghost_disabled,
      },
      padding: buttonSemanticTokens.button_padding_base,
      gap: buttonSemanticTokens.button_gap,
      fontFamily: buttonSemanticTokens.button_font_family,
      fontSize: buttonSemanticTokens.button_font_size_base,
      fontWeight: buttonSemanticTokens.button_font_weight_medium,
      lineHeight: buttonSemanticTokens.button_line_height_base,
      borderRadius: buttonSemanticTokens.radius_button_standard,
      borderWidth: buttonSemanticTokens.button_border_width,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    primaryLarge: {
      fontSize: buttonSemanticTokens.button_font_size_large,
      lineHeight: buttonSemanticTokens.button_line_height_large,
      padding: buttonSemanticTokens.button_padding_large,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    primaryMedium: {
      fontSize: buttonSemanticTokens.button_font_size_medium,
      lineHeight: buttonSemanticTokens.button_line_height_medium,
      padding: buttonSemanticTokens.button_padding_medium,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    primarySmall: {
      fontSize: buttonSemanticTokens.button_font_size_small,
      lineHeight: buttonSemanticTokens.button_line_height_small,
      padding: buttonSemanticTokens.button_padding_small,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    primaryXSmall: {
      fontSize: "font_size_xs",
      lineHeight: "line_height_xs",
      padding: "spacing_xs spacing_md",
      loadingIconSize: "font_size_xs",
    },
    secondaryLarge: {
      fontSize: buttonSemanticTokens.button_font_size_large,
      lineHeight: buttonSemanticTokens.button_line_height_large,
      padding: buttonSemanticTokens.button_padding_large,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    secondaryMedium: {
      fontSize: buttonSemanticTokens.button_font_size_medium,
      lineHeight: buttonSemanticTokens.button_line_height_medium,
      padding: buttonSemanticTokens.button_padding_medium,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    secondarySmall: {
      fontSize: buttonSemanticTokens.button_font_size_small,
      lineHeight: buttonSemanticTokens.button_line_height_small,
      padding: buttonSemanticTokens.button_padding_small,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    secondaryXSmall: {
      fontSize: "font_size_xs",
      lineHeight: "line_height_xs",
      padding: "spacing_xs spacing_md",
      loadingIconSize: "font_size_xs",
    },
    tertiaryLarge: {
      fontSize: buttonSemanticTokens.button_font_size_large,
      lineHeight: buttonSemanticTokens.button_line_height_large,
      padding: buttonSemanticTokens.button_padding_large,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    tertiaryMedium: {
      fontSize: buttonSemanticTokens.button_font_size_medium,
      lineHeight: buttonSemanticTokens.button_line_height_medium,
      padding: buttonSemanticTokens.button_padding_medium,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
      gap: buttonSemanticTokens.button_gap,
    },
    tertiarySmall: {
      fontSize: buttonSemanticTokens.button_font_size_small,
      lineHeight: buttonSemanticTokens.button_line_height_small,
      padding: buttonSemanticTokens.button_padding_small,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    destructiveLarge: {
      fontSize: buttonSemanticTokens.button_font_size_large,
      lineHeight: buttonSemanticTokens.button_line_height_large,
      padding: buttonSemanticTokens.button_padding_large,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    destructiveMedium: {
      fontSize: buttonSemanticTokens.button_font_size_medium,
      lineHeight: buttonSemanticTokens.button_line_height_medium,
      padding: buttonSemanticTokens.button_padding_medium,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    destructiveSmall: {
      fontSize: buttonSemanticTokens.button_font_size_small,
      lineHeight: buttonSemanticTokens.button_line_height_small,
      padding: buttonSemanticTokens.button_padding_small,
      loadingIconSize: buttonSemanticTokens.button_loading_icon_size,
    },
    destructiveXSmall: {
      fontSize: "font_size_xs",
      lineHeight: "line_height_xs",
      padding: "spacing_xs spacing_md",
      loadingIconSize: "font_size_xs",
    },
    buttonIcon: {
      default: {
        backgroundColor: buttonIconSemanticTokens.color_buttonIcon_bg_enabled,
        borderColor: buttonIconSemanticTokens.color_buttonIcon_border_enabled,
        iconColor: buttonIconSemanticTokens.color_buttonIcon_icon_enabled,
      },
      hover: {
        backgroundColor: buttonIconSemanticTokens.color_buttonIcon_bg_hover,
        borderColor: buttonIconSemanticTokens.color_buttonIcon_border_hover,
        iconColor: buttonIconSemanticTokens.color_buttonIcon_icon_hover,
      },
      disabled: {
        backgroundColor: buttonIconSemanticTokens.color_buttonIcon_bg_disabled,
        borderColor: buttonIconSemanticTokens.color_buttonIcon_border_disabled,
        iconColor: buttonIconSemanticTokens.color_buttonIcon_icon_disabled,
      },
      destructive: {
        default: {
          backgroundColor:
            buttonIconSemanticTokens.color_buttonIcon_bg_destructive_enabled,
          borderColor:
            buttonIconSemanticTokens.color_buttonIcon_border_destructive_enabled,
          iconColor:
            buttonIconSemanticTokens.color_buttonIcon_icon_destructive_enabled,
        },
        hover: {
          backgroundColor:
            buttonIconSemanticTokens.color_buttonIcon_bg_destructive_hover,
          borderColor:
            buttonIconSemanticTokens.color_buttonIcon_border_destructive_hover,
          iconColor:
            buttonIconSemanticTokens.color_buttonIcon_icon_destructive_hover,
        },
        disabled: {
          backgroundColor:
            buttonIconSemanticTokens.color_buttonIcon_bg_destructive_disabled,
          borderColor:
            buttonIconSemanticTokens.color_buttonIcon_border_destructive_disabled,
          iconColor:
            buttonIconSemanticTokens.color_buttonIcon_icon_destructive_disabled,
        },
      },
      borderRadius: buttonIconSemanticTokens.radius_buttonIcon_standard,
    },
  },
  semanticColors: {
    textPrimary: colorTextSemanticTokens.color_text_primary,
    textSecondary: colorTextSemanticTokens.color_text_secondary,
    textTertiary: colorTextSemanticTokens.color_text_tertiary,
    textPlaceholder: colorTextSemanticTokens.color_text_placeholder,
    textError: colorTextSemanticTokens.color_text_error,
    textWarning: colorTextSemanticTokens.color_text_warning,
    textSuccess: colorTextSemanticTokens.color_text_success,
    textInfo: colorTextSemanticTokens.color_text_info,
    textInverse: colorTextSemanticTokens.color_text_inverse,
    textBrand: colorTextSemanticTokens.color_text_brand,
    bgSurfaceDefault: colorBgUiSemanticTokens.color_bg_surface_standard,
    bgSurfaceSubtle: colorBgUiSemanticTokens.color_bg_surface_subtle,
    bgSurfaceDark: colorBgUiSemanticTokens.color_bg_surface_dark,
    bgSurfaceDisabled: colorBgUiSemanticTokens.color_bg_surface_disabled,
    bgSurfaceError: colorBgUiSemanticTokens.color_bg_surface_error,
    bgSurfaceWarning: colorBgUiSemanticTokens.color_bg_surface_warning,
    bgSurfaceSuccess: colorBgUiSemanticTokens.color_bg_surface_success,
    bgSurfaceInfo: colorBgUiSemanticTokens.color_bg_surface_info,
    bgSurfaceBrand: colorBgUiSemanticTokens.color_bg_surface_brand,
    bgOverlay: colorBgUiSemanticTokens.color_bg_overlay,
    borderLightest: colorBorderSemanticTokens.color_border_lightest,
    borderLighter: colorBorderSemanticTokens.color_border_lighter,
    borderLight: colorBorderSemanticTokens.color_border_light,
    borderDark: colorBorderSemanticTokens.color_border_dark,
    borderInputDefault: colorBorderSemanticTokens.color_border_input,
    borderInputActive: colorBorderSemanticTokens.color_border_input_active,
    borderError: colorBorderSemanticTokens.color_border_error,
    iconPrimary: colorIconSemanticTokens.color_icon_primary,
    iconSecondary: colorIconSemanticTokens.color_icon_secondary,
    iconDisabled: colorIconSemanticTokens.color_icon_disabled,
    iconWhite: colorIconSemanticTokens.color_icon_white,
    iconSuccess: colorIconSemanticTokens.color_icon_success,
    iconError: colorIconSemanticTokens.color_icon_error,
    iconBrand: colorIconSemanticTokens.color_icon_brand,
  },
  primitiveTokens: {
    fontFamilies: fontFamilyTokens,
    fontWeights: fontWeightTokens,
    fontSizes: fontSizeTokens,
    lineHeights: lineHeightTokens,
    colors: colorPrimitiveTokens,
    spacing: spacingTokens,
    radii: radiusTokens,
    borders: borderPrimitiveTokens,
    widths: widthTokens,
  },
  disabledGlobal: {
    backgroundColor: "color_bg_surface_disabled",
    textColor: "color_text_placeholder",
    opacity: "0.6",
  },
};

export const AG3Theme: DefaultTheme = {
  // == Legacy Properties ==
  brandColor: "#0090E3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryBG: "#ffffff",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryBG: "#f5f7f8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryBG: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryButtonBG: "#0090e303",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryButtonHover: "#0090e305",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryButtonActive: "#005688",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryButtonBG: "#F5F7F8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryButtonHover: "#F5F7F8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryButtonActive: "#F5F7F8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryButtonBG: "#ffffff",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryButtonSelected: "#ffffff",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  destructiveButtonBG: "transparent",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  destructiveButtonHover: "transparent",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  destructiveButtonActive: "transparent",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  disabledButtonBG: "#f2f2f2",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  disabledInput: "#f2f2f2",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  toggleBG: "#cfd1d4",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  activeToggleBG: "#1fb45a",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  badgeColor: "#1fb45a",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  successIconColor: "#1fb45a",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  errorIconColor: "#cb2020",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  badgeTextColor: "#ffffff",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  footerBg: "#f5f7f8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryInputBG: "#9fa4aa",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tooltipBG: "#60676f",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryTextColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryTextColor: "#6f777f",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryTextColor: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  invertTextColor: "#ffffff",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  warningTextColor: "#d44800",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  destructiveTextColor: "#cb2020",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  navLinkColor: "#3f4a55",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  navIconActive: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  navLinkActiveColor: "#ffffff",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryLinkColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryLinkHover: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryLinkColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryLinkHover: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryLinkColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryLinkHover: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  disabledLinkColor: "#0f1d2b50",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryButtonTextColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryButtonTextColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryButtonTextColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  destructiveButtonTextColor: "#cb2020",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  disabledButtonTextColor: "#6f777f",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryIconColor: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryIconColor: "#b6c7d4",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryIconColor: "#9fa4aa",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryIconBackground: "#eff4f8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryBorder: "#cfd1d4",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryBorder: "#e7e8ea",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  tertiaryBorder: "#0f1d2b",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  primaryButtonBorder: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryButtonBorder: "#F5F7F8",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  secondaryButtonHoverBorder: "#E7E8EA",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  selectedBorder: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  errorColor: "#cb2020",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  shadowColor: "#00000040",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  overlayColor: "#00395a95",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  publicNavBorderActive: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  publicNavBorder: "#e7e8ea",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  publicLogoBGColor: "#0090e3",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  publicLogoUrl: "",
  /** @deprecated Use semantic tokens from themeTokenCategories instead */
  portalLogoUrl: "",
  theme_name: "AG3Theme",
  ...themeDefaults,

  // == New Top-Level Token Categories ==
  ...themeTokenCategories, // Spread the fully populated categories

  // == New Top-Level Resolvers and Token Map ==
  allTokensForTheme: allTokens,
  resolve<T extends keyof typeof import("./designTokens").allTokens | string>(
    tokenNameOrValue: T
  ): string {
    return getTokenValue(tokenNameOrValue as string, this.allTokensForTheme);
  },
  resolveSizeToNumber<
    T extends keyof typeof import("./designTokens").allTokens | string
  >(tokenNameOrValue: T): number {
    const resolvedValue = getTokenValue(
      tokenNameOrValue as string,
      this.allTokensForTheme
    );
    const num = parseFloat(resolvedValue);
    return isNaN(num) ? 0 : num;
  },

  // == Token categories for structured access ==
  tokens: themeTokenCategories,
};
